// Portfolio growth against MOEX total return indexes (docs/performance/SPEC.md). The server sends each
// account's daily value and external flow; here accounts are added up for the chosen view and the
// time-weighted return is computed for the chosen period (decision 2).

export const INDEX_CODES = ['MCFTRR', 'MCFTR', 'RGBITR'];

// Rosstat consumer prices, sent as one more index: a daily price level (decisions 26–28).
export const INFLATION_CODE = 'CPI';

export const PERIODS = ['1m', '3m', '6m', '1y', 'ytd', 'lastYear', 'all'];

const DAY = 86400000;

function parseDay(date) {
  return Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)));
}

export function dayString(ms) {
  return new Date(ms).toISOString().slice(0, 10);
}

function addMonths(date, months) {
  const d = new Date(parseDay(date));
  const day = d.getUTCDate();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  // 31 March − 1 month → 28/29 February, not 3 March.
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(day, last));
  return dayString(d.getTime());
}

// Days of the view: the accounts' values and flows added up per date. Whole accounts only: a composite
// portfolio with chosen securities is drawn by its accounts (decision 5).
// liveToday: today's value with live prices, put in place of the rebuilt one.
export function viewSeries(data, view, liveToday = null) {
  const ids = new Set(view.members.map((m) => m.portfolio_id));
  const accounts = (data?.portfolios ?? []).filter((p) => ids.has(p.id));
  const withRows = accounts.filter((p) => p.start && p.values.length > 0);
  if (withRows.length === 0) {
    return null;
  }

  const start = withRows.map((p) => p.start).sort()[0];
  const startMs = parseDay(start);
  const end = withRows.map((p) => dayString(parseDay(p.start) + (p.values.length - 1) * DAY)).sort().at(-1);
  const length = Math.round((parseDay(end) - startMs) / DAY) + 1;
  const values = new Array(length).fill(0);
  const flows = new Array(length).fill(0);
  const inflows = new Array(length).fill(0);
  for (const account of withRows) {
    const offset = Math.round((parseDay(account.start) - startMs) / DAY);
    account.values.forEach((v, i) => { values[offset + i] += v; });
    account.flows.forEach((f, i) => { flows[offset + i] += f; });
    (account.inflows ?? []).forEach((f, i) => { inflows[offset + i] += f; });
  }
  if (liveToday !== null && Number.isFinite(liveToday) && end === data.today) {
    values[length - 1] = liveToday;
  }

  const dates = Array.from({ length }, (_, i) => dayString(startMs + i * DAY));
  // Money put in minus money taken out, from the first day.
  let running = 0;
  const invested = flows.map((f) => (running += f));

  return {
    start,
    end,
    dates,
    values,
    flows,
    inflows,
    invested,
    status: accounts.some((p) => p.status === 'pending') ? 'pending' : accounts.some((p) => p.status === 'error') ? 'error' : 'ready',
    mismatches: accounts.reduce((n, p) => n + (p.check?.positions?.length ?? 0), 0),
  };
}

// The period as [base day, last day]: returns run from the close of the base day. The base is the day
// before the period, so "this year" starts from 31 December.
export function periodRange(period, today, firstDay) {
  const year = Number(today.slice(0, 4));
  let base;
  let end = today;
  switch (period) {
    case '1m': base = addMonths(today, -1); break;
    case '3m': base = addMonths(today, -3); break;
    case '6m': base = addMonths(today, -6); break;
    case '1y': base = addMonths(today, -12); break;
    case 'ytd': base = `${year - 1}-12-31`; break;
    case 'lastYear': base = `${year - 2}-12-31`; end = `${year - 1}-12-31`; break;
    default: base = dayString(parseDay(firstDay) - DAY);
  }
  // Before the first day the account held nothing: start from the day before it.
  const earliest = dayString(parseDay(firstDay) - DAY);
  return { base: base < earliest ? earliest : base, end: end > today ? today : end, clipped: base < earliest };
}

// Value, flows and invested of a day; before the first day everything is zero.
function at(series, date) {
  const i = Math.round((parseDay(date) - parseDay(series.start)) / DAY);
  if (i < 0) {
    return { value: 0, flow: 0, inflow: 0, invested: 0 };
  }
  const j = Math.min(i, series.values.length - 1);
  const same = i === j;
  return { value: series.values[j], flow: same ? series.flows[j] : 0, inflow: same ? series.inflows[j] : 0, invested: series.invested[j] };
}

// Below this a day's return is not counted: a nearly empty account turns kopecks into ±90 % (decision 20).
const MIN_BASE = 1000;

// Index close on a date: the last one on or before it. A price level (inflation) is not known after its
// last date — there it is null, not flat.
function indexClose(index, date, openEnded = true) {
  const { dates, closes } = index;
  if (!openEnded && date > dates.at(-1)) {
    return null;
  }
  let lo = 0;
  let hi = dates.length - 1;
  let found = null;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (dates[mid] <= date) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found === null ? null : closes[found];
}

// The same money held in a level (an index or prices) for a day: inflows from the start of the day, outflows
// at its end.
function shadowStep(shadow, day, ratio) {
  return (shadow + day.inflow) * ratio + (day.flow - day.inflow);
}

// Day-to-day growth of the price level; null after its last known date. Before its first date prices are
// taken as flat.
function priceRatio(inflation, date, previousDate) {
  if (date > inflation.dates.at(-1)) {
    return null;
  }
  const now = indexClose(inflation, date, false);
  const before = indexClose(inflation, previousDate, false);
  return now && before ? now / before : 1;
}

// "Invested + inflation" at the end of a day: every deposit and withdrawal since the first day grown with
// prices from its own day (decision 28). Null when prices of that day are not known yet.
function investedRealAt(series, inflation, date) {
  if (date > inflation.dates.at(-1)) {
    return null;
  }
  let real = 0;
  const startMs = parseDay(series.start);
  const days = Math.round((parseDay(date) - startMs) / DAY);
  for (let k = 0; k <= days; k++) {
    const day = dayString(startMs + k * DAY);
    real = shadowStep(real, at(series, day), k > 0 ? priceRatio(inflation, day, dayString(startMs + (k - 1) * DAY)) : 1);
  }
  return real;
}

// Points of the period: date, value, invested, portfolio TWR % and index % from the base day, and income in
// roubles since the base day for the portfolio and for the same money kept in the index.
// A day's return is (V_t − V_{t−1} − F_t) / (V_{t−1} + In_t): money coming in or out is not growth,
// inflows work from the start of the day, outflows leave at its end (decisions 2, 20).
// The index "shadow" starts with the portfolio's base value and gets the same deposits and withdrawals on the
// same days, under the same convention (decision 23).
// inflation: the price level (or null) — adds the inflation % and roubles, and "invested + inflation".
export function periodPoints(series, index, range, inflation = null) {
  const points = [];
  const indexBase = index ? indexClose(index, range.base) : null;
  const priceBase = inflation ? indexClose(inflation, range.base, false) : null;
  const start = at(series, range.base);
  let growth = 1;
  let previous = start.value;
  let shadow = start.value;
  let previousClose = indexBase;
  let priceShadow = priceBase ? start.value : null;
  let investedReal = inflation ? investedRealAt(series, inflation, range.base) : null;
  const baseMs = parseDay(range.base);
  const days = Math.round((parseDay(range.end) - baseMs) / DAY);
  for (let k = 0; k <= days; k++) {
    const date = dayString(baseMs + k * DAY);
    const day = at(series, date);
    const close = index ? indexClose(index, date) : null;
    const price = inflation ? indexClose(inflation, date, false) : null;
    if (k > 0) {
      const base = previous + day.inflow;
      if (base >= MIN_BASE) {
        growth *= 1 + (day.value - previous - day.flow) / base;
      }
      previous = day.value;
      const ratio = close !== null && previousClose ? close / previousClose : 1;
      shadow = shadowStep(shadow, day, ratio);
      previousClose = close ?? previousClose;
      const growthOfPrices = inflation ? priceRatio(inflation, date, dayString(baseMs + (k - 1) * DAY)) : null;
      priceShadow = priceShadow !== null && growthOfPrices !== null ? shadowStep(priceShadow, day, growthOfPrices) : null;
      investedReal = investedReal !== null && growthOfPrices !== null ? shadowStep(investedReal, day, growthOfPrices) : null;
    }
    const netFlow = day.invested - start.invested;
    points.push({
      date,
      value: day.value,
      invested: day.invested,
      portfolio: (growth - 1) * 100,
      index: close !== null && indexBase ? (close / indexBase - 1) * 100 : null,
      income: day.value - start.value - netFlow,
      indexIncome: indexBase ? shadow - start.value - netFlow : null,
      inflation: price !== null && priceBase ? (price / priceBase - 1) * 100 : null,
      inflationIncome: priceShadow !== null ? priceShadow - start.value - netFlow : null,
      investedReal,
    });
  }
  return points;
}

// Headline numbers of the period: value change, net money in, income (change minus money in), and the same
// for the index shadow.
export function periodSummary(points) {
  if (points.length === 0) {
    return null;
  }
  const first = points[0];
  const last = points.at(-1);
  return {
    value: last.value,
    invested: last.invested,
    income: last.income,
    netFlow: last.invested - first.invested,
    portfolio: last.portfolio,
    index: last.index,
    indexIncome: last.indexIncome,
    inflation: inflationSummary(points),
  };
}

// Inflation is published with a lag, so it is compared on its last known day — the portfolio too.
function inflationSummary(points) {
  let last = null;
  for (const p of points) {
    if (p.inflation !== null) {
      last = p;
    }
  }
  if (!last) {
    return null;
  }
  return {
    date: last.date,
    percent: last.inflation,
    income: last.inflationIncome,
    investedReal: last.investedReal,
    portfolio: last.portfolio,
    portfolioIncome: last.income,
  };
}
